<template>
    <div>
        <page-header :text="$t('registration.account')" />

        <div class="container">
            <div class="row">
                <div class="column-wide-12 brick-zero">
                    <!-- user status -->
                    <div class="row brick-mobile--tiny--bottom slab-mobile--bottom divider--bottom">
                        <div class="column-mobile-6 brick-mobile--dwarf--top brick-mobile--dwarf--bottom">
                            {{ $t('registration.user_status') }}
                        </div>
                        <div class="column-mobile-18 brick-mobile--dwarf--top brick-mobile--dwarf--bottom align-right">
                            <b v-if="user.pro == 3">{{ $t('registration.user_status_oem') }}</b>
                            <b v-if="user.pro == 2">{{ $t('registration.user_status_pro') }}</b>
                            <b v-if="user.pro == 1">{{ $t('registration.user_status_semi_pro') }}</b>
                            <b v-if="!user.pro">{{ $t('registration.user_status_private') }}</b>
                        </div>
                    </div>

                    <!-- user data -->
                    <div class="row brick-mobile--tiny--bottom slab-mobile--grand--bottom divider--bottom">
                        <div class="alert alert-warning" role="alert" v-show="userData.canChange === false">
                            {{ $t('registration.sso_note_account') }}
                        </div>
                        <div class="alert alert-success" role="alert" v-show="responseCode === 200">
                            {{ $t('registration.changes_saved') }}
                        </div>
                        <div class="alert alert-danger" role="alert" v-show="responseCode === 422">
                            {{ $t('registration.changes_not_saved') }}
                        </div>
                        <user-form v-if="userData !== false" v-on:send="sendUserForm" v-bind:user="userData" v-bind:initialEmpty="false" ref="userform" :ssoUser="!userData.canChange"></user-form>
                        <div class="row form__row">
                            <div class="column-mobile-24 align-right brick-mobile--small">
                                <button type="submit" class="button button--primary" :class="{ disabled: !formChanged }" @click.prevent="sendUserForm">
                                    {{ $t('buttons.save_changes') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- change password form -->
                    <div class="row brick-mobile--tiny--bottom slab-mobile--bottom">
                        <form name="passwordForm">
                            <fieldset class="form__group">
                                <div class="alert alert-warning" role="alert" v-show="userData.canChange === false">
                                    {{ $t('registration.sso_note_password') }}
                                </div>
                                <div class="alert alert-success" role="alert" v-show="passwordForm.responseCode === 200">
                                    {{ $t('registration.password_changed') }}
                                </div>
                                <div class="alert alert-danger" role="alert" v-show="passwordForm.responseCode === 400">
                                    {{ $t('registration.current_password_not_correct') }}
                                </div>
                                <div class="alert alert-danger" role="alert" v-show="passwordForm.responseCode === 422">
                                    {{ $t('registration.password_not_saved') }}
                                </div>

                                <div class="row form__row">
                                    <label for="name" class="column-mobile-6 form__label">{{ $t('fields.current_password') }}</label>

                                    <div class="column-mobile-18">
                                        <input
                                            type="password"
                                            class="form__field"
                                            id="current_password"
                                            name="current_password"
                                            :placeholder="$t('fields.current_password')"
                                            v-model.trim="$v.passwordForm.currentPassword.$model"
                                            :disabled="userData.id === ''"
                                        />

                                        <div>
                                            <p class="form__error" v-show="!$v.passwordForm.currentPassword.required && $v.passwordForm.currentPassword.$dirty">
                                                {{ $t('fields.password_e_required') }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="row form__row">
                                    <label for="name" class="column-mobile-6 form__label">{{ $t('fields.new_password') }}</label>

                                    <div class="column-mobile-18">
                                        <input
                                            type="password"
                                            class="form__field"
                                            id="password"
                                            name="password"
                                            :placeholder="$t('fields.new_password')"
                                            v-model.trim="$v.passwordForm.newPassword.$model"
                                            style="margin-bottom:5px;"
                                            :disabled="userData.id === ''"
                                        />

                                        <input
                                            type="password"
                                            class="form__field"
                                            id="password_repeat"
                                            name="password_repeat"
                                            :placeholder="$t('fields.new_password_repeat')"
                                            v-model.trim="$v.passwordForm.newPasswordRepeat.$model"
                                            :disabled="userData.id === ''"
                                        />

                                        <div>
                                            <br />
                                            <p
                                                class="form__error"
                                                :class="{
                                                    'form__error--success': $v.passwordForm.newPassword.minLength && passwordForm.newPassword.length > 0,
                                                }"
                                            >
                                                •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.minlength') }}</span>
                                            </p>
                                            <p
                                                class="form__error"
                                                :class="{
                                                    'form__error--success': $v.passwordForm.newPassword.bigSmall,
                                                }"
                                            >
                                                •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.bigsmall') }}</span>
                                            </p>
                                            <p
                                                class="form__error"
                                                :class="{
                                                    'form__error--success': $v.passwordForm.newPassword.nr,
                                                }"
                                            >
                                                •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.number') }}</span>
                                            </p>
                                            <p
                                                class="form__error"
                                                :class="{
                                                    'form__error--success': $v.passwordForm.newPassword.extra,
                                                }"
                                            >
                                                •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.extra') }}</span>
                                            </p>
                                            <p
                                                class="form__error"
                                                :class="{
                                                    'form__error--success': $v.passwordForm.newPassword.sameAsPassword && passwordForm.newPassword.length > 0,
                                                }"
                                            >
                                                •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.samepassword') }}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>

                            <div class="row form__row">
                                <div class="column-mobile-24 align-right brick-mobile--small">
                                    <button type="submit" class="button button--primary" :class="{ disabled: $v.$invalid }" @click.prevent="sendPasswordHandler">
                                        {{ $t('buttons.change_password') }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div class="column-wide-12">
                    <div class="row">
                        <h2 class="brick-zero--left brick-zero--right brick-wide--top">
                            {{ $t('devices.title') }}
                        </h2>
                        <user-device-list />
                    </div>
                </div>
            </div>

            <div v-if="user.accessible_groups">
                <p class="login-welcome">{{ $t('fields.group') }}</p>
                <p>{{ user.accessible_groups.join(', ') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import UserForm from '@/components/UserForm';
import PageHeader from '@/components/PageHeader';
import UserDeviceList from '@/components/UserDeviceList';
import { required, minLength, sameAs, maxLength } from 'vuelidate/lib/validators';

const nr = value => /[0-9]/.test(value);
const bigSmall = value => /[a-z]/.test(value) && /[A-Z]/.test(value);
const extra = value => /[^0-9a-zA-Z ]/.test(value);

export default {
    components: {
        UserForm,
        PageHeader,
        UserDeviceList,
    },
    data() {
        return {
            user: {},
            userData: false,
            responseCode: false,
            passwordForm: {
                currentPassword: '',
                newPassword: '',
                newPasswordRepeat: '',
                responseCode: false,
            },
            ignoreUserChange: true,
            formChanged: false,
        };
    },
    watch: {
        userData: {
            deep: true,
            handler() {
                if (this.ignoreUserChange === false) {
                    this.formChanged = true;
                }
                this.$forceUpdate();
            },
        },
    },
    mounted() {
        this.$store.dispatch('loadUserData');
        this.user = this.$store.state.user;
        this.userData = this.$store.state.userData;

        // api.users.grafanaCookie();

        const that = this;
        setTimeout(function() {
            that.ignoreUserChange = false;
        }, 1000);
    },
    validations() {
        let validations = {
            passwordForm: {
                currentPassword: {
                    required,
                },
                newPassword: {
                    required,
                    minLength: minLength(12),
                    maxLength: maxLength(128),
                    sameAsPassword: sameAs(function() {
                        return this.passwordForm.newPasswordRepeat;
                    }),
                    nr,
                    bigSmall,
                    extra,
                },
                newPasswordRepeat: {
                    required,
                },
            },
        };

        return validations;
    },
    methods: {
        sendUserForm() {
            const that = this;

            if (this.$refs.userform.$v.$invalid) {
                this.$refs.userform.$v.$touch();
                return;
            }

            api.users.update({ user: this.$refs.userform.user }).then(
                () => {
                    this.responseCode = 200;
                    this.$store.dispatch('loadUserData').then(() => {
                        that.formChanged = false;
                    });
                },
                () => {
                    this.responseCode = 422;
                }
            );
        },
        sendPasswordHandler() {
            if (this.$v.$invalid) {
                this.$v.$touch();
                return;
            }

            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('devices.password_change_logout_all'),
                    cancelText: this.$t('common.no'),
                    confirmText: this.$t('common.yes'),
                    successCallback: () => {
                        this.sendPasswordForm(false);
                    },
                    cancelCallback: () => {
                        this.sendPasswordForm(true);
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        sendPasswordForm(stayLoggedIn) {
            api.users
                .updatePassword({
                    current_password: this.passwordForm.currentPassword,
                    password: this.passwordForm.newPassword,
                    keep_devices_signed_in: stayLoggedIn,
                })
                .then(
                    () => {
                        this.passwordForm.responseCode = 200;
                    },
                    error => {
                        this.passwordForm.responseCode = error.response.status;
                    }
                );
        },
    },
};
</script>

<style lang="scss" scoped>
.button {
    &.disabled {
        background: #ccc;
    }
}
</style>
